import React from "react";
import AppRecoveryScreen from "@/components/recovery/AppRecoveryScreen";
import {
  clearPanicRoot,
  renderMinimalFallbackShell,
} from "@/theme/startup-safe-render";

type AppErrorBoundaryProps = {
  children: React.ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
};

type FallbackGuardState = {
  failed: boolean;
};

/** If the branded fallback itself throws, paint the non-React minimal shell. */
export class FallbackRenderGuard extends React.Component<
  { children: React.ReactNode },
  FallbackGuardState
> {
  state: FallbackGuardState = { failed: false };

  static getDerivedStateFromError(): Partial<FallbackGuardState> {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console -- nested containment
    console.error("[app.error-boundary.fallback]", error, info.componentStack);
    try {
      renderMinimalFallbackShell();
    } catch (shellError) {
      // eslint-disable-next-line no-console -- nested containment
      console.error("[app.error-boundary] minimal shell paint failed", shellError);
    }
  }

  render(): React.ReactNode {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

/** Clears any imperative shell once the React recovery card has committed. */
function ReactRecoveryOwnership({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  React.useEffect(() => {
    clearPanicRoot();
  }, []);
  return <>{children}</>;
}

/**
 * Top-level storefront boundary — a render crash must never leave a blank white screen.
 * Fallback is CSS-only (no MUI theme dependency).
 */
export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): Partial<AppErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console -- startup / crash telemetry
    console.error("[app.error-boundary]", error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <FallbackRenderGuard>
          <ReactRecoveryOwnership>
            <AppRecoveryScreen />
          </ReactRecoveryOwnership>
        </FallbackRenderGuard>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
